/**
 * ExecutiveTrendChart — recharts line chart for the Guardian Score trend
 * across the reporting period, framed with editorial chrome.
 *
 * Reuses the same recharts primitives the legacy page used (so we don't
 * regress the trend visual) but moves them inside an editorial section
 * (eyebrow + italic headline + sub) and tightens the surface to the dark-navy
 * card the rest of the redesign uses. Theme-aware via getThemeColors().
 *
 * Empty / sparse handling: when fewer than 2 data points carry a non-null
 * Guardian Score, we render a quiet "Not enough history" placeholder rather
 * than dropping the section entirely (the executive shouldn't have to wonder
 * whether the chart is broken or simply missing).
 */

import { useMemo } from 'react'
import { format } from 'date-fns'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useTheme } from '@/context/ThemeContext'
import { getThemeColors } from '@/lib/themeColors'
import type { ExecutiveReportData } from '@/types/executiveReport'

interface Props {
  data: ExecutiveReportData['guardianScoreTrend']
  /** Reporting window in days — surfaced in the section sub-line. */
  periodDays: number
}

export default function ExecutiveTrendChart({ data, periodDays }: Props) {
  // Subscribe to theme so chart colors track the dark/light toggle.
  useTheme()
  const tc = getThemeColors()

  const chartData = useMemo(
    () =>
      data
        .filter(d => d.guardianScore != null)
        .map(d => ({
          date: d.date,
          score: d.guardianScore,
          label: format(new Date(d.date), 'MMM d'),
        })),
    [data],
  )

  const hasEnoughHistory = chartData.length >= 2

  return (
    <section className="mt-12">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Section · 02 · Trajectory
      </div>
      <h2
        className="mt-1 font-display font-extrabold tracking-[-0.018em] text-[28px]"
        style={{ color: 'rgb(var(--color-text-primary))' }}
      >
        Where Guardian Score{' '}
        <em
          style={{
            fontStyle: 'italic',
            fontWeight: 600,
            color: 'rgb(var(--color-primary-700))',
          }}
        >
          went.
        </em>
      </h2>
      <p
        className="mt-2 max-w-2xl text-[14px] italic"
        style={{ color: 'rgb(var(--color-text-secondary))' }}
      >
        Daily composite over the {periodDays}-day window. The line tells you
        whether the period closed stronger than it opened.
      </p>

      <div
        className="mt-6 rounded-lg border bg-dark-800 p-6"
        style={{ borderColor: 'rgb(var(--color-dark-600))' }}
      >
        {hasEnoughHistory ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={tc.chartGrid} />
              <XAxis dataKey="label" stroke={tc.chartAxisLabel} fontSize={11} />
              <YAxis domain={[0, 100]} stroke={tc.chartAxisLabel} fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: tc.surface,
                  border: `1px solid ${tc.border}`,
                  borderRadius: 8,
                }}
                labelStyle={{ color: tc.muted }}
                itemStyle={{ color: tc.primary }}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke={tc.primary}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 5, fill: tc.primary }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div
            className="flex h-[260px] items-center justify-center text-[14px] italic"
            style={{ color: 'rgb(var(--color-text-secondary))' }}
          >
            Not enough history to chart. Guardian needs at least two daily
            score points before drawing the line.
          </div>
        )}
      </div>
    </section>
  )
}
